import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CARD_HEIGHT } from "./card-classes";
import { cardRowOffsetPx } from "./cards";
import {
  CARD_WINDOW_OVERSCAN_PX,
  type CardWindowBounds,
  cardPrefixCount,
  cardWindowBounds,
  cardWindowIndexes,
} from "./cards-window";
import { LIST_SCROLL_ATTRIBUTE } from "./list-scroll";
import { rootFontPx } from "./root-font";

export interface CardWindow {
  /**
   * Индексы карточек, которые рисуются, по возрастанию. `null` — окна нет
   * (статический рендер, вид вне области прокрутки): рисуются все карточки.
   */
  indexes: readonly number[] | null;
  /**
   * Прокрутить область так, чтобы строка `rowIndex` карточки `cardIndex` встала
   * посередине полосы. Центрируется строка, а не карточка: карточка монеты с
   * множеством строк бывает выше полосы.
   */
  revealRow: (cardIndex: number, rowIndex: number) => void;
}

/**
 * Окно карточек (DEN-104, design stage9-cards-virtual Д1–Д3, Д5): какие
 * карточки рисовать по прокрутке области `[data-list-scroll]`. Видимая полоса
 * меряется по прямоугольникам области и контейнера, а не по `scrollTop`: так
 * не нужно отдельно знать отступ контейнера внутри области.
 *
 * Вид перерисовывается, только когда граница окна переходит через шаг
 * (`cardWindowBounds`), а не на каждое событие прокрутки. `pinned` —
 * закреплённые карточки, которые рисуются вне окна. `rowCounts` — число строк
 * карточек: по нему выбираются карточки первого кадра, пока раскладки нет.
 */
export function useCardWindow(
  container: HTMLElement | null,
  tops: readonly number[],
  heights: readonly number[],
  rowCounts: readonly number[],
  pinned: readonly number[],
): CardWindow {
  const [scroller, setScroller] = useState<HTMLElement | null>(null);
  const [bounds, setBounds] = useState<CardWindowBounds | null>(null);

  const measure = useCallback(() => {
    if (container === null || scroller === null) {
      return;
    }
    const viewTop = scroller.getBoundingClientRect().top - container.getBoundingClientRect().top;
    const next = cardWindowBounds(viewTop, viewTop + scroller.clientHeight);
    setBounds((previous) =>
      previous !== null && previous.top === next.top && previous.bottom === next.bottom
        ? previous
        : next,
    );
  }, [container, scroller]);

  // Layout-эффект, а не обычный: окно первого кадра считается до отрисовки.
  // Контейнер снят (вид опустел) — прежнее окно сбрасывается: новый контейнер
  // начнётся с другой прокрутки, и старая полоса ему не годится.
  useLayoutEffect(() => {
    const next = container?.closest<HTMLElement>(`[${LIST_SCROLL_ATTRIBUTE}]`) ?? null;
    setScroller(next);
    if (next === null) {
      setBounds(null);
    }
  }, [container]);

  useLayoutEffect(() => {
    if (scroller === null || container === null) {
      return;
    }
    measure();
    scroller.addEventListener("scroll", measure, { passive: true });
    // Область — её высота; контейнер — его собственный размер и сдвиг вслед
    // за ним.
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(scroller);
    observer?.observe(container);
    return () => {
      scroller.removeEventListener("scroll", measure);
      observer?.disconnect();
    };
  }, [scroller, container, measure]);

  // Раскладка сменилась — высота контейнера иная, и прокрутка могла
  // сдвинуться без события `scroll` (браузер подрезал её по новой высоте).
  // biome-ignore lint/correctness/useExhaustiveDependencies: перемер по смене раскладки
  useLayoutEffect(measure, [measure, tops]);

  const indexes = useMemo(() => {
    if (tops.length > 0) {
      // Раскладка есть, а области прокрутки нет — окна нет, рисуются все.
      return bounds === null ? null : cardWindowIndexes(tops, heights, bounds, pinned);
    }
    // Раскладки ещё нет. В браузере — первый кадр до layout-эффектов: хватит
    // карточек на высоту окна с запасом. Без DOM (статический рендер) — все.
    if (typeof window === "undefined" || rowCounts.length === 0) {
      return null;
    }
    const count = cardPrefixCount(
      rowCounts,
      rootFontPx(),
      window.innerHeight + 2 * CARD_WINDOW_OVERSCAN_PX,
    );
    return Array.from({ length: count }, (_, index) => index);
  }, [bounds, tops, heights, rowCounts, pinned]);

  const latest = useRef({ container, scroller, tops });
  latest.current = { container, scroller, tops };
  const revealRow = useCallback((cardIndex: number, rowIndex: number) => {
    const { container: box, scroller: area, tops: cardTops } = latest.current;
    const top = cardTops[cardIndex];
    if (box === null || area === null || top === undefined) {
      return;
    }
    const viewTop = area.getBoundingClientRect().top - box.getBoundingClientRect().top;
    const rowTop = top + cardRowOffsetPx(rowIndex, rootFontPx());
    area.scrollTop += rowTop - viewTop - (area.clientHeight - CARD_HEIGHT.rowPx) / 2;
  }, []);

  return { indexes, revealRow };
}
